<template>
  <div class="search-results">
    <skeleton-placeholder v-if="loading" class="search-results__preloading-skeleton" />

    <div
      v-else-if="!loading && searchQuery && searchResults.length === 0"
      :class="{
        'no-results-placeholder': true,
        'search-results__no-results-placeholder': true,
      }"
    >
      <div class="no-results-placeholder__title">
        <div class="no-results-placeholder__title-icon" v-html="noSearchResultsIcon"></div>
        Sorry, nothing was found
      </div>

      No results for “{{ searchQuery }}”, please try something else
    </div>

    <template v-else>
      <ul class="search-results__results-sections-list">
        <!-- Result section -->
        <li
          :class="{
            'result-section': true,
            'search-results__results-section': true,
          }"
        >
          <!-- items list -->
          <ul class="result-section__items-list">
            <li
              v-for="(item, index) in searchResults"
              :key="item.id"
              :class="{
                'result-item': true,
                'result-section__result-item': true,
                'result-item--highlighted': index === highlightedIndex,
              }"
            >
              <fs-router-link
                v-slot="{ href, navigate }"
                :to="{ name: 'product', params: { id: item.id } }"
                :custom="true"
              >
                <a
                  :ref="(el) => setItemRef(el, index)"
                  v-prefetch="() => useProducts().prefetchProduct(item.id)"
                  class="result-item__link"
                  tabindex="-1"
                  :href="href"
                  @click="goToItem(navigate, $event)"
                >
                  <!-- Product -->
                  <div class="result-item__photo-container">
                    <span
                      :class="{
                        'result-item__photo': true,
                        'result-item__photo--placeholder': true,
                      }"
                      v-html="nodeIcon(item.hierarchyNodeId)"
                    />
                  </div>

                  <div class="result-item__name-n-article-number">
                    <div class="result-item__name">
                      {{ item.name }}
                    </div>
                    <div class="result-item__number">
                      {{ item.productNumber }}
                    </div>
                  </div>

                  <div
                    :class="{
                      'search-item-type': true,
                      'result-item__search-item-type': true,
                    }"
                  >
                    <div class="search-item-type__icon" v-html="nodeIcon(item.hierarchyNodeId)"></div>
                    <div class="search-item-type__name">
                      {{
                        item.hierarchyNodeId
                          ? useHierarchy().fullNodesById[item.hierarchyNodeId].fullNameOrName
                          : 'Product'
                      }}
                    </div>
                  </div>

                  <div
                    :class="{
                      'articles-sublist': true,
                      'result-item__articles-sublist': true,
                    }"
                  >
                    <ul v-if="item.colors.length" class="articles-sublist__sublist">
                      <li
                        v-for="(productColor, index) in item.colors.slice(0, 4)"
                        :key="index"
                        :class="{
                          'single-article': true,
                          'articles-sublist__single-article': true,
                        }"
                      >
                        <div class="single-article__photo-container">
                          <img
                            v-if="productColor.image"
                            class="single-article__photo"
                            :src="imagePath(productColor.image, 200, 170, 'fit')"
                            :alt="item.name"
                          />

                          <span
                            v-else
                            :class="{
                              'single-article__photo': true,
                              'single-article__photo--placeholder': true,
                            }"
                            v-html="nodeIcon(item.hierarchyNodeId)"
                          />
                        </div>

                        <fs-pill-color class="single-article__color" :color="productColor.color" />
                      </li>
                    </ul>

                    <div
                      v-else
                      :class="{
                        'articles-sublist__sublist': true,
                        'articles-sublist__sublist--no-results': true,
                      }"
                    >
                      <div class="articles-sublist__no-result-icon" v-html="noResultsIcon"></div>
                      No Articles added yet
                    </div>

                    <div v-if="item.totalColorCount - 4 > 0" class="articles-sublist__how-many-more-counter">
                      +{{ item.totalColorCount - 4 }}
                    </div>
                  </div>

                  <div class="result-item__info-arrow-btn" v-html="arrowRightIcon"></div>
                </a>
              </fs-router-link>
            </li>
          </ul>
          <!-- / items list -->

          <div v-if="hasMore || loadingMore" ref="sentinelRef" class="search-results__loading-more">
            <skeleton-placeholder v-if="loadingMore" class="search-results__loading-more-skeleton" />
          </div>
        </li>
        <!-- / Result section -->
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useProducts } from '@/store/products';
import { imagePath } from '@lib/imgproxy';
import FsPillColor from '@components/pills/fs-pill-color.vue';
import SkeletonPlaceholder from '@components/search-results/skeleton-placeholder.vue';
import arrowRightIcon from '@icons/other/arrow-right.svg';
import noResultsIcon from '@icons/miscellaneous/no-results.svg';
import noSearchResultsIcon from '@icons/other2/no-search-results.svg';
import type { QuickSearchProductDto } from '@/ApiClient.ts';
import { ref, watch, onBeforeUnmount } from 'vue';
import type { NavigationFailure } from 'vue-router';
import { useHierarchy } from '@/store/hierarchy.ts';
import FsRouterLink from '@components/fs-router-link.vue';

defineOptions({ name: 'search-results' });

const {
  closeAndClear,
  loading = false,
  loadingMore = false,
  hasMore = false,
  searchResults = [],
  searchQuery,
  highlightedIndex = -1,
} = defineProps<{
  closeAndClear: Function;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  searchResults: QuickSearchProductDto[];
  searchQuery: string;
  highlightedIndex?: number;
}>();

const emit = defineEmits<{
  'load-more': [];
}>();

const itemRefs = ref<HTMLAnchorElement[]>([]);

function setItemRef(el: unknown, index: number) {
  if (el instanceof HTMLAnchorElement) itemRefs.value[index] = el;
  else delete itemRefs.value[index];
}

watch(
  () => highlightedIndex,
  (index) => itemRefs.value[index]?.scrollIntoView({ block: 'nearest' })
);

defineExpose({
  activateHighlighted() {
    itemRefs.value[highlightedIndex]?.click();
  },
});

const sentinelRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinelRef, (el) => {
  observer?.disconnect();
  if (!el) return;
  observer = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting) emit('load-more');
  });
  observer.observe(el);
});

onBeforeUnmount(() => observer?.disconnect());

async function goToItem(navigate: (e?: MouseEvent) => Promise<void | NavigationFailure>, event: MouseEvent) {
  if (event.button !== 0 || event.ctrlKey || event.metaKey) return;

  event.preventDefault();

  await navigate();
  closeAndClear();
}

function nodeIcon(hierarchyNodeId?: number) {
  return useHierarchy().getIconWithFallback(hierarchyNodeId);
}
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Single article -------------------------------------------------------------
.single-article {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  align-items: center;

  &__photo-container {
    flex-grow: 1;
    width: 8rem;
    margin-bottom: 0.6rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
  }

  &__photo {
    max-width: 6rem;
    max-height: 4rem;

    &--placeholder {
      display: flex;
      justify-content: center;
      align-content: center;
      align-items: center;

      :deep(svg) {
        width: 100%;
        height: 100%;
        max-height: 4rem;
        fill: rgba($c-dark, 0.5);
      }
    }
  }

  &__color {
    width: 7.5rem;
  }
}

// Articles sublist -----------------------------------------------------------
.articles-sublist {
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  align-content: center;
  align-items: center;

  &__sublist {
    padding: 0;
    margin: 0 1rem 0 0;
    display: flex;
    justify-content: flex-end;
    align-content: stretch;
    align-items: stretch;
    list-style: none;

    &--no-results {
      font-size: 1.4rem;
      color: rgba($c-dark, 0.5);
    }
  }

  &__no-result-icon {
    width: 1.9rem;
    height: 1.9rem;
    margin: -3px 0.8rem 0 0;

    :deep(svg) {
      fill: rgba($c-dark, 0.5);
    }
  }

  &__single-article {
    margin-right: 0.4rem;
  }

  &__how-many-more-counter {
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.4rem 0 3px;
    box-sizing: border-box;
    border: 1px rgba($c-dark, 0.5) solid;
    border-radius: 10rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    font-size: 1rem;
    line-height: 1rem;
    font-weight: bold;
    color: rgba($c-dark, 0.5);
  }
}

// Result item ----------------------------------------------------------------
.result-item {
  padding: 1.3rem 0 1.3rem 1.5rem;
  border-bottom: 1px rgba($c-dark, 0.1) solid;
  box-sizing: border-box;
  display: flex;
  position: relative;
  user-select: none;

  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 10;
    background: rgba(0, 0, 0, 0.02);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease;
  }

  &:first-child {
    &::before {
      border-radius: 1rem 1rem 0 0;
    }
  }

  &:last-child {
    &::before {
      border-radius: 0 0 1rem 1rem;
    }
  }

  &:hover,
  &--highlighted {
    &::before {
      opacity: 1;
    }
  }

  &--highlighted {
    &::after {
      content: '';
      width: 3px;
      height: 100%;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: 11;
      background: $c-accent;
    }
  }

  &__link {
    width: 100%;
    height: 100%;
    display: flex;
    align-content: center;
    align-items: center;
    text-decoration: none;
    color: $c-dark;
  }

  &__photo-container {
    flex-grow: 1;
    width: 10%;
    margin-right: 2.5rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
  }

  &__photo {
    max-width: 10rem;
    max-height: 8.5rem;

    &--placeholder {
      max-width: 4rem;
      max-height: 4rem;
      display: flex;
      justify-content: center;
      align-content: center;
      align-items: center;

      :deep(svg) {
        width: 100%;
        height: 100%;
        max-height: 6.5rem;
        fill: rgba($c-dark, 0.5);
      }
    }
  }

  &__name-n-article-number {
    width: 22%;
    margin-right: 1.8rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    align-items: center;
  }

  &__name {
    margin-bottom: 1px;
    font-size: 1.6rem;
    line-height: 2.2rem;
    text-align: center;
  }

  &__number {
    font-size: 1rem;
    line-height: 1.4rem;
    text-align: center;
    color: rgba($c-dark, 0.5);
  }

  &__search-item-type {
    width: 11%;
  }
  .search-item-type {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    align-items: center;

    &__icon {
      width: 2.5rem;
      max-height: 1.5rem;
      margin-bottom: 0.4rem;
      display: flex;
      justify-content: center;
      align-content: center;
      align-items: center;

      :deep(svg) {
        width: 2.5rem;
        max-width: 100%;
        height: 1.5rem;
        max-height: 100%;
        fill: rgba($c-dark, 0.5);
      }
    }

    &__name {
      font-size: 1rem;
      line-height: 1rem;
      color: rgba($c-dark, 0.5);
      text-transform: uppercase;
      text-align: center;
    }
  }

  &__articles-sublist {
    width: 43%;
    margin-right: 3rem;
  }

  &__info-arrow-btn {
    width: 1.8rem;
    height: 1.8rem;
    margin-right: 2rem;

    :deep(svg) {
      fill: $c-dark;
    }
  }
}

// Result section -------------------------------------------------------------
.result-section {
  &__title {
    margin-bottom: 0.7rem;
    display: flex;
    align-content: center;
    align-items: center;
    font: 400 2rem/2rem 'Montserrat';
  }

  &__title-icon {
    width: 2rem;
    height: 2rem;
    margin-right: 3px;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;

    :deep(svg) {
      fill: $c-dark;
    }
  }

  &__items-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__result-item {
    &:last-child {
      border-bottom: none;
    }
  }
}

// No results placeholder -----------------------------------------------------
.no-results-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: rgba($c-dark, 0.5);
  text-align: center;

  &__title {
    margin-bottom: 1.4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: 'Montserrat', sans-serif;
    font-size: 2.4rem;
  }

  &__title-icon {
    width: 2.7rem;
    height: 2.7rem;
    margin-right: 1.5rem;

    :deep(svg) {
      fill: rgba($c-dark, 0.5);
    }
  }
}

// Search results -------------------------------------------------------------
.search-results {
  width: 100rem;
  padding: 2rem 2rem;
  box-sizing: border-box;

  &__no-results-placeholder {
    padding: 4rem 0;
  }

  &__results-sections-list {
    padding: 0;
    margin: 0;
    max-height: 60rem;
    overflow-y: auto;
    list-style: none;
  }

  &__results-section {
  }

  &__loading-more {
    padding: 1.5rem 0;
  }

  &__loading-more-skeleton {
    height: 6rem;
  }
}
</style>
